import type { DashboardKeepItem } from '@/supabase/order/types/dashboardSummary';

// 킵 상품 목록(서버 계약: 상품 기준으로 접힌 (상품, 닉네임, 수량) 페어)을
// 고객 기준으로 다시 접는 순수 변환. 서버 변경 없음, 날짜는 다루지 않는다(스펙 §4).
//
// 정렬은 화면의 가변 sortConfig가 아니라 고정 규칙을 쓴다 — 같은 데이터 = 같은 파일(스펙 §3.3).
// JSONB 키(nicknames) 순회 순서에 절대 의존하지 않는다.

export interface KeepProductEntry {
  name: string;
  totalQuantity: number;
  /** 수량 desc → 닉네임 asc */
  nicknames: { nickname: string; quantity: number }[];
}

export interface KeepCustomerEntry {
  nickname: string;
  totalQuantity: number;
  /** 수량 desc → 상품명 asc */
  items: { name: string; quantity: number }[];
}

export interface KeepListView {
  /** 총 수량 desc → 상품명 asc */
  products: KeepProductEntry[];
  /** 총 수량 desc → 닉네임 asc */
  customers: KeepCustomerEntry[];
}

/** 화면(고객별 보기)과 엑셀(2시트)이 공유하는 단일 소스. 입력을 mutate하지 않는다. */
export function buildKeepListView(keepItems: DashboardKeepItem[]): KeepListView {
  const customerMap = new Map<string, Map<string, number>>();

  const products: KeepProductEntry[] = keepItems.map((item) => {
    const nicknames = Object.entries(item.nicknames ?? {}).map(([nickname, quantity]) => ({ nickname, quantity }));
    nicknames.sort((a, b) => b.quantity - a.quantity || a.nickname.localeCompare(b.nickname));

    for (const { nickname, quantity } of nicknames) {
      let items = customerMap.get(nickname);
      if (!items) {
        items = new Map();
        customerMap.set(nickname, items);
      }
      items.set(item.name, (items.get(item.name) ?? 0) + quantity);
    }

    return { name: item.name, totalQuantity: item.totalQuantity, nicknames };
  });
  products.sort((a, b) => b.totalQuantity - a.totalQuantity || a.name.localeCompare(b.name));

  const customers: KeepCustomerEntry[] = [...customerMap].map(([nickname, itemMap]) => {
    const items = [...itemMap].map(([name, quantity]) => ({ name, quantity }));
    items.sort((a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name));
    return { nickname, totalQuantity: items.reduce((sum, i) => sum + i.quantity, 0), items };
  });
  customers.sort((a, b) => b.totalQuantity - a.totalQuantity || a.nickname.localeCompare(b.nickname));

  return { products, customers };
}
